import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Heart, ArrowLeft, Package, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function Wishlist() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/wishlist`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : [])
            .then(setItems)
            .finally(() => setLoading(false));
    }, []);

    const removeItem = async (itemId) => {
        try {
            const res = await fetch(`${API}/wishlist/${itemId}`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                setItems(prev => prev.filter(w => w.item_id !== itemId));
                toast.success('Removed from wishlist');
            }
        } catch { toast.error('Failed'); }
    };

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <Button size="sm" variant="ghost" onClick={() => navigate('/shop')} className="mb-2 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Shop
                    </Button>
                    <span className="label-text text-plasma-red">SAVED</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="wishlist-title">
                        Wishlist
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">{items.length} saved items</p>
                </motion.div>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
                ) : items.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-8 text-center">
                            <Heart size={48} className="text-muted-foreground mx-auto mb-4" />
                            <p className="font-medium text-foreground">Your wishlist is empty</p>
                            <p className="text-sm text-muted-foreground mt-1">Tap the heart icon on any product to save it here</p>
                            <Button onClick={() => navigate('/shop')} className="mt-4 btn-primary">Browse Shop</Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {items.map(w => {
                            const item = w.item;
                            return (
                                <motion.div key={w.item_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                                    <Card className="glass-card cursor-pointer hover:border-primary/20 transition-colors"
                                        data-testid={`wishlist-item-${w.item_id}`}>
                                        <CardContent className="p-3 flex items-center gap-3">
                                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted shrink-0"
                                                onClick={() => navigate(`/shop/item/${w.item_id}`)}>
                                                {item?.image_url ? (
                                                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <Package size={24} className="text-muted-foreground" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0" onClick={() => navigate(`/shop/item/${w.item_id}`)}>
                                                <p className="font-bold text-sm text-foreground truncate">{item?.name || w.item_name}</p>
                                                {item?.brand && <p className="text-xs text-muted-foreground">{item.brand}</p>}
                                                <p className="font-mono text-neon-gold text-sm mt-0.5">{item?.price_boltz?.toLocaleString()} Boltz</p>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => removeItem(w.item_id)}
                                                className="text-muted-foreground hover:text-plasma-red shrink-0"
                                                data-testid={`remove-${w.item_id}`}>
                                                <Trash size={16} />
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
